import {
  createFileRoute,
  useRouteContext,
  Navigate,
} from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link } from "@tanstack/react-router";
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { z } from "zod";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import {
  Search,
  User,
  Loader2,
  AlertCircle,
  RefreshCw,
  SlidersHorizontal,
  FilterX,
  Pencil,
  Briefcase,
  UserCheck2,
  ChevronDown,
  Eye,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { useOrganization } from "@/hooks/use-organization";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTRPC, useTRPCClient } from "@/trpc";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ChatDialog } from "@/features/chat/chat-dialog";
import { ProfileModal } from "@/features/profile/components/ProfileModal";
import { InterestedJobPostsDialog } from "@/features/job-post/components/interested-job-posts-dialog";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { useOrgPath } from "@/hooks/useOrgPath";
import { RevealProviderDialog } from "@/features/provider/reveal-provider-dialog";
import {
  AffiliationEndReason,
  AffiliationStatus,
} from "@api/shared-runtime/enums";
import {
  ChangeProviderStatusDialog,
  type ProviderRosterStatus,
} from "@/features/affiliation/change-provider-status-dialog";
import { Label } from "@/components/ui/label";
import { Storage } from "@/common/utils/storage";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PROVIDER_CHAT_UNAVAILABLE_MESSAGE } from "@api/shared-runtime/provider-chat-availability";
import { EndedProvidersList } from "./_components/ended-providers-list";

const PROVIDERS_PAGE_SIZE = 100;
const PROVIDER_FILTERS_STORAGE_PREFIX = "provider-directory-filters";

const PROVIDER_STATUS_FILTERS = ["all", "hired", "candidate", "ended"] as const;

type ProviderStatusFilter = (typeof PROVIDER_STATUS_FILTERS)[number];

const parseProviderStatusFilter = (
  value: string | undefined,
): ProviderStatusFilter =>
  PROVIDER_STATUS_FILTERS.find((status) => status === value) ?? "all";

const PROVIDER_STATUS_FILTER_LABELS: Record<ProviderStatusFilter, string> = {
  all: "All statuses",
  hired: "Hired",
  candidate: "Candidate",
  ended: "Ended",
};

const providerFilterSearchSchema = z.object({
  q: z.string().optional(),
  specialty: z.string().optional(),
  owner: z.enum(["anyone", "me"]).optional(),
  status: z.enum(PROVIDER_STATUS_FILTERS).optional(),
});

type ProviderFilterSearch = z.infer<typeof providerFilterSearchSchema>;

type ProviderFilters = {
  q: string;
  specialty: string;
  owner: "anyone" | "me";
  status: ProviderStatusFilter;
};

const normalizeProviderFilters = (
  search: ProviderFilterSearch,
): ProviderFilters => ({
  q: search.q?.trim() ?? "",
  specialty: search.specialty?.trim() ?? "",
  owner: search.owner === "me" ? "me" : "anyone",
  status: parseProviderStatusFilter(search.status),
});

const providerFiltersToSearch = (filters: ProviderFilters) => ({
  q: filters.q.trim() || undefined,
  specialty: filters.specialty || undefined,
  owner: filters.owner === "me" ? filters.owner : undefined,
  status: filters.status === "all" ? undefined : filters.status,
});

const hasProviderFilterSearch = (search: ProviderFilterSearch) =>
  Boolean(search.q || search.specialty || search.owner || search.status);

const getProviderFiltersStorageKey = (userId: string) =>
  `${PROVIDER_FILTERS_STORAGE_PREFIX}:${userId}`;

const parseStoredProviderFilters = (value: string | null) => {
  if (!value) {
    return null;
  }
  try {
    const result = providerFilterSearchSchema.safeParse(JSON.parse(value));
    return result.success ? normalizeProviderFilters(result.data) : null;
  } catch {
    return null;
  }
};

function ProviderActionsMenu({
  displayName,
  isHidden,
  canChat,
  canChangeStatus,
  isRevealChecking,
  onReveal,
  onChat,
  onViewJobInterests,
  onChangeStatus,
}: {
  displayName: string;
  isHidden: boolean;
  canChat: boolean;
  canChangeStatus: boolean;
  isRevealChecking: boolean;
  onReveal: () => void;
  onChat: () => void;
  onViewJobInterests: () => void;
  onChangeStatus: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          aria-label={`Actions for ${displayName}`}
        >
          Actions
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {isHidden ? (
          <DropdownMenuItem
            className="gap-3 text-amber-700 focus:text-amber-700"
            disabled={isRevealChecking}
            onSelect={onReveal}
          >
            {isRevealChecking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
            Reveal provider
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            className="gap-3"
            disabled={!canChat}
            title={canChat ? undefined : PROVIDER_CHAT_UNAVAILABLE_MESSAGE}
            onSelect={onChat}
          >
            <MessageSquare className="h-4 w-4" />
            Chat
          </DropdownMenuItem>
        )}
        <DropdownMenuItem className="gap-3" onSelect={onViewJobInterests}>
          <Briefcase className="h-4 w-4" />
          Job interests
        </DropdownMenuItem>
        {canChangeStatus && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-3" onSelect={onChangeStatus}>
              <UserCheck2 className="h-4 w-4" />
              Change status
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProvidersAllPage() {
  const { user } = useRouteContext({ from: "/_org" }) as {
    user: { id?: string; is_admin: boolean } | null | undefined;
  };
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const searchFilters = normalizeProviderFilters(search);
  const { hasPermission } = useRolePermissions(user);
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();
  const { selectedOrgBusinessUnit } = useOrganization();
  const canManage = hasPermission(Permission.ManageJobPosts);
  const canManageAffiliations = hasPermission(Permission.ManageAffiliations);
  const orgPath = useOrgPath();
  const [chattingProvider, setChattingProvider] = useState<{
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);
  const [viewingProvider, setViewingProvider] = useState<{
    id: string;
  } | null>(null);
  const [viewingJobPostsProvider, setViewingJobPostsProvider] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [storageHydrated, setStorageHydrated] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchFilters.q);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState(
    searchFilters.specialty,
  );
  const [assignedToMe, setAssignedToMe] = useState(
    searchFilters.owner === "me",
  );
  const [providerStatusFilter, setProviderStatusFilter] =
    useState<ProviderStatusFilter>(searchFilters.status);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [statusTarget, setStatusTarget] = useState<{
    userId: string;
    name: string;
    currentStatus: AffiliationStatus;
    canMarkHired: boolean;
  } | null>(null);
  const [selectedProviderStatus, setSelectedProviderStatus] =
    useState<ProviderRosterStatus>(AffiliationStatus.Candidate);
  const [revealTarget, setRevealTarget] = useState<string | null>(null);
  const [revealCheckingProviderId, setRevealCheckingProviderId] = useState<
    string | null
  >(null);
  const [initialMonthlyRevealLimit, setInitialMonthlyRevealLimit] = useState<
    number | null
  >(null);
  const [endReason, setEndReason] = useState<AffiliationEndReason>(
    AffiliationEndReason.MarkedInError,
  );
  const [statusStartDate, setStatusStartDate] = useState("");
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const storageHydratedUserRef = useRef<string | null>(null);

  const openRevealDialog = async (providerUserId: string) => {
    if (!selectedOrgBusinessUnit?.id || revealCheckingProviderId) {
      return;
    }

    setRevealCheckingProviderId(providerUserId);
    try {
      const availability =
        await trpcClient.organization.getProviderRevealAvailability.query({
          organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        });
      setInitialMonthlyRevealLimit(
        availability.limitReached ? availability.monthlyLimit : null,
      );
    } catch {
      // The reveal mutation remains authoritative when the preflight fails.
      setInitialMonthlyRevealLimit(null);
    } finally {
      setRevealCheckingProviderId(null);
      setRevealTarget(providerUserId);
    }
  };

  const updateProviderFilterSearch = useCallback(
    (filters: Partial<ProviderFilters>) => {
      const nextFilters = {
        q: searchTerm,
        specialty: selectedSpecialty,
        owner: assignedToMe ? ("me" as const) : ("anyone" as const),
        status: providerStatusFilter,
        ...filters,
      };

      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          ...providerFiltersToSearch(nextFilters),
        }),
      });
    },
    [
      assignedToMe,
      providerStatusFilter,
      navigate,
      searchTerm,
      selectedSpecialty,
    ],
  );

  useEffect(() => {
    setSearchTerm(searchFilters.q);
    setSelectedSpecialty(searchFilters.specialty);
    setAssignedToMe(searchFilters.owner === "me");
    setProviderStatusFilter(searchFilters.status);
  }, [
    searchFilters.owner,
    searchFilters.q,
    searchFilters.specialty,
    searchFilters.status,
  ]);

  useEffect(() => {
    let cancelled = false;

    const hydrateStoredFilters = async () => {
      if (!user?.id) {
        setStorageHydrated(true);
        return;
      }

      if (storageHydratedUserRef.current === user.id) {
        setStorageHydrated(true);
        return;
      }

      storageHydratedUserRef.current = user.id;

      if (hasProviderFilterSearch(search)) {
        setStorageHydrated(true);
        return;
      }

      const storedFilters = parseStoredProviderFilters(
        await Storage.getItem(getProviderFiltersStorageKey(user.id)),
      );

      if (cancelled) {
        return;
      }

      if (storedFilters) {
        await navigate({
          replace: true,
          search: (prev) => ({
            ...prev,
            ...providerFiltersToSearch(storedFilters),
          }),
        });
      }

      if (!cancelled) {
        setStorageHydrated(true);
      }
    };

    void hydrateStoredFilters();

    return () => {
      cancelled = true;
    };
  }, [navigate, search, user?.id]);

  useEffect(() => {
    if (!storageHydrated || !user?.id) {
      return;
    }

    const filtersForStorage = normalizeProviderFilters(search);
    void Storage.setItem(
      getProviderFiltersStorageKey(user.id),
      JSON.stringify(providerFiltersToSearch(filtersForStorage)),
    );
  }, [search, storageHydrated, user?.id]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      updateProviderFilterSearch({ q: searchTerm.trim() });
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchTerm, updateProviderFilterSearch]);

  const isEndedView = providerStatusFilter === "ended";

  const providerListInput = useMemo(
    () => ({
      organizationBusinessUnitId: selectedOrgBusinessUnit?.id,
      source: "affiliation" as const,
      searchTerm: debouncedSearchTerm || undefined,
      specialtyCode: selectedSpecialty || undefined,
      assignedToUserId: assignedToMe ? user?.id : undefined,
      affiliation:
        providerStatusFilter === "hired" || providerStatusFilter === "candidate"
          ? providerStatusFilter
          : undefined,
    }),
    [
      assignedToMe,
      debouncedSearchTerm,
      providerStatusFilter,
      selectedOrgBusinessUnit?.id,
      selectedSpecialty,
      user?.id,
    ],
  );

  const {
    data: providersData,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["profileListInfinite", providerListInput],
    queryFn: ({ pageParam = 0 }) =>
      trpcClient.profile.list.query({
        ...providerListInput,
        limit: PROVIDERS_PAGE_SIZE,
        offset: pageParam * PROVIDERS_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) =>
      Array.isArray(lastPage) && lastPage.length === PROVIDERS_PAGE_SIZE
        ? allPages.length
        : undefined,
    initialPageParam: 0,
    enabled: !!selectedOrgBusinessUnit?.id && !isEndedView,
  });

  const providers = useMemo(
    () => providersData?.pages.flatMap((page) => page) ?? [],
    [providersData],
  );

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "300px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const { data: teamMembers } = useQuery(
    trpc.organization.getTeamMembers.queryOptions(
      {
        organizationBusinessUnitId: selectedOrgBusinessUnit?.id ?? "",
      },
      { enabled: !!selectedOrgBusinessUnit?.id && canManage },
    ),
  );

  const invalidateProviders = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.profile.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: ["profileListInfinite"],
      }),
    ]);
  };

  const updateAssignedUser = useMutation({
    mutationFn: (input: {
      organizationBusinessUnitAffiliationId: string;
      organizationBusinessUnitId: string;
      assignedUserId: string | null;
    }) => trpcClient.profileShare.updateAssignedUser.mutate(input),
    onSuccess: invalidateProviders,
  });

  const closeStatusDialog = () => {
    setStatusTarget(null);
    setSelectedProviderStatus(AffiliationStatus.Candidate);
    setStatusStartDate("");
    setEndReason(AffiliationEndReason.MarkedInError);
  };

  const markNotHiredMutate = useMutation(
    trpc.organizationBusinessUnitAffiliation.markNotHired.mutationOptions({
      onSuccess: async () => {
        closeStatusDialog();
        await invalidateProviders();
        toast.success("Provider status changed to candidate.");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to change provider status.");
      },
    }),
  );

  const markHiredMutate = useMutation(
    trpc.organizationBusinessUnitAffiliation.markHired.mutationOptions({
      onSuccess: async () => {
        closeStatusDialog();
        await invalidateProviders();
        toast.success("Provider status changed to hired.");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to change provider status.");
      },
    }),
  );

  const endAffiliationMutate = useMutation(
    trpc.organizationBusinessUnitAffiliation.endAffiliation.mutationOptions({
      onSuccess: async () => {
        closeStatusDialog();
        await invalidateProviders();
        await queryClient.invalidateQueries({
          queryKey:
            trpc.organizationBusinessUnitAffiliation.listEnded.pathKey(),
        });
        if (selectedOrgBusinessUnit?.id) {
          await queryClient.invalidateQueries({
            queryKey:
              trpc.organizationBusinessUnitAffiliation.listInvitations.queryKey(
                {
                  organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                },
              ),
          });
        }
        toast.success("Provider status changed to ended.");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to change provider status.");
      },
    }),
  );

  const isStatusSaving =
    markNotHiredMutate.isPending ||
    markHiredMutate.isPending ||
    endAffiliationMutate.isPending;

  const saveProviderStatus = () => {
    if (!statusTarget || !selectedOrgBusinessUnit?.id) {
      return;
    }
    const input = {
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      userId: statusTarget.userId,
    };

    if (selectedProviderStatus === AffiliationStatus.Hired) {
      markHiredMutate.mutate({
        ...input,
        ...(statusStartDate ? { startDate: statusStartDate } : {}),
      });
      return;
    }
    if (selectedProviderStatus === AffiliationStatus.Candidate) {
      markNotHiredMutate.mutate(input);
      return;
    }
    endAffiliationMutate.mutate({ ...input, endReason });
  };

  const filteredProviders = providers;

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedSpecialty("");
    setAssignedToMe(false);
    setProviderStatusFilter("all");
    if (user?.id) {
      void Storage.removeItem(getProviderFiltersStorageKey(user.id));
    }
    updateProviderFilterSearch({
      q: "",
      specialty: "",
      owner: "anyone",
      status: "all",
    });
  };

  const hasFilters = Boolean(
    searchTerm.trim() ||
      selectedSpecialty ||
      assignedToMe ||
      providerStatusFilter !== "all",
  );
  const activeFilterCount = [
    searchTerm.trim(),
    selectedSpecialty,
    assignedToMe,
    providerStatusFilter !== "all",
  ].filter(Boolean).length;

  const renderFilterControls = (variant: "desktop" | "mobile") => (
    <div
      className={
        variant === "desktop"
          ? "grid grid-cols-1 gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(180px,256px)_minmax(150px,180px)_minmax(150px,180px)]"
          : "space-y-4"
      }
    >
      <div className="space-y-1.5">
        <Label
          htmlFor={`provider-search-${variant}`}
          className="text-xs font-medium text-slate-600"
        >
          Search
        </Label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-4 w-4 text-slate-400" />
          </div>
          <Input
            id={`provider-search-${variant}`}
            placeholder="Provider name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-slate-600">Specialty</Label>
        <SpecialtiesSelect
          value={selectedSpecialty}
          isMulti={false}
          onChange={(value) => {
            const specialty = typeof value === "string" ? value : "";
            setSelectedSpecialty(specialty);
            updateProviderFilterSearch({ specialty });
          }}
          placeholder="Any specialty"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-slate-600">Owner</Label>
        <Select
          value={assignedToMe ? "me" : "anyone"}
          onValueChange={(value) => {
            const owner = value === "me" ? "me" : "anyone";
            setAssignedToMe(owner === "me");
            updateProviderFilterSearch({ owner });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="anyone">Anyone</SelectItem>
            <SelectItem value="me">Assigned to me</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {selectedOrgBusinessUnit?.id && (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-slate-600">Status</Label>
          <Select
            value={providerStatusFilter}
            onValueChange={(value) => {
              const status = parseProviderStatusFilter(value);
              setProviderStatusFilter(status);
              updateProviderFilterSearch({ status });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="hired">Hired</SelectItem>
              <SelectItem value="candidate">Candidate</SelectItem>
              <SelectItem value="ended">Ended</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-slate-900">
            Provider management
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">
            Review shared providers and connect with the right clinicians for
            your open roles.
          </p>
        </div>

        <div className="space-y-3">
          <div className="hidden lg:block">
            {renderFilterControls("desktop")}
          </div>

          <div className="lg:hidden">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  <span className="inline-flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4" />
                    Filters
                  </span>
                  {activeFilterCount > 0 && (
                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-50">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 rounded-md border border-slate-200 bg-white p-3 shadow-sm">
                {renderFilterControls("mobile")}
                {hasFilters && (
                  <Button
                    variant="outline"
                    className="mt-5 w-full"
                    onClick={handleResetFilters}
                  >
                    <FilterX className="mr-2 h-4 w-4" />
                    Clear filters
                  </Button>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>

          {hasFilters && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-slate-500">
                <FilterX className="h-3.5 w-3.5" />
                Active filters
              </span>
              {searchTerm && (
                <Badge
                  variant="outline"
                  className="border-blue-200 text-blue-700"
                >
                  Search: {searchTerm}
                </Badge>
              )}
              {selectedSpecialty && (
                <Badge
                  variant="outline"
                  className="border-blue-200 text-blue-700"
                >
                  Specialty: {selectedSpecialty}
                </Badge>
              )}
              {assignedToMe && (
                <Badge
                  variant="outline"
                  className="border-blue-200 text-blue-700"
                >
                  Owner: Assigned to me
                </Badge>
              )}
              {providerStatusFilter !== "all" && (
                <Badge
                  variant="outline"
                  className="border-blue-200 text-blue-700"
                >
                  Status: {PROVIDER_STATUS_FILTER_LABELS[providerStatusFilter]}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-3 text-slate-500 hover:text-slate-700"
                onClick={handleResetFilters}
              >
                <FilterX className="mr-1 h-3.5 w-3.5" />
                Clear all
              </Button>
            </div>
          )}
        </div>

        {isEndedView && selectedOrgBusinessUnit?.id && (
          <EndedProvidersList
            organizationBusinessUnitId={selectedOrgBusinessUnit.id}
            search={debouncedSearchTerm}
            specialtyCode={selectedSpecialty}
            assignedToUserId={assignedToMe ? user?.id : undefined}
          />
        )}

        {!isEndedView &&
          (isLoading ? (
            <Card className="border-dashed">
              <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-blue-100 p-3 rounded-full mb-4">
                  <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">
                  Loading providers
                </h3>
                <p className="text-gray-600 mb-6 max-w-md text-sm sm:text-base">
                  Please wait while we fetch the latest providers for your
                  organization.
                </p>
              </CardContent>
            </Card>
          ) : isError ? (
            <Card className="border-dashed border-2 border-red-300 bg-red-50">
              <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-red-100 p-3 rounded-full mb-4">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">
                  Failed to load providers
                </h3>
                <p className="text-gray-600 mb-6 max-w-md text-sm sm:text-base">
                  {error instanceof Error
                    ? error.message
                    : "An error occurred while loading providers. Please try again."}
                </p>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => refetch()}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : filteredProviders.length > 0 ? (
            <>
              {/* Card Layout (mobile + tablet) */}
              <div className="block lg:hidden space-y-4 min-w-0">
                {filteredProviders.map((provider) => {
                  const isHidden = provider.isRevealed === false;
                  const displayName = isHidden
                    ? `Provider #${provider.userId.slice(-4)}`
                    : `${provider.firstName ?? ""} ${provider.lastName ?? ""}`.trim();

                  return (
                    <Card
                      key={provider.id}
                      className="rounded-2xl border border-slate-200/80 bg-white shadow-sm"
                    >
                      <CardContent className="space-y-4 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <Avatar className="h-12 w-12 flex-shrink-0">
                              <AvatarImage src="" alt="Provider" />
                              <AvatarFallback className="bg-gray-200">
                                <User className="h-6 w-6 text-gray-500" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="w-0 flex-1">
                              <Button
                                variant="link"
                                className="text-blue-600 hover:text-blue-700 font-semibold p-0 h-auto text-left justify-start text-lg max-w-full"
                                onClick={() => {
                                  setViewingProvider({
                                    id: provider.userId,
                                  });
                                }}
                              >
                                <span className="truncate">{displayName}</span>
                              </Button>
                              {provider.isHired && (
                                <div className="mt-1">
                                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">
                                    Hired
                                  </Badge>
                                </div>
                              )}
                              <div className="mt-2 space-y-2">
                                {!isHidden && (
                                  <p className="text-sm text-slate-600">
                                    <span className="font-semibold text-slate-900">
                                      Primary specialty:
                                    </span>{" "}
                                    {provider.specialties[0]?.name ||
                                      "Not provided"}
                                  </p>
                                )}
                                {!isHidden && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Pools
                                    </span>
                                    <div className="mt-1.5">
                                      {provider.pools &&
                                      provider.pools.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                          {provider.pools
                                            .slice(0, 2)
                                            .map((pool) => (
                                              <Link
                                                to={orgPath(
                                                  "/providers/pools/" + pool.id,
                                                )}
                                                key={pool.id}
                                              >
                                                <Badge
                                                  variant="outline"
                                                  className="text-xs"
                                                >
                                                  {pool.name}
                                                </Badge>
                                              </Link>
                                            ))}
                                          {provider.pools.length > 2 && (
                                            <Badge
                                              variant="outline"
                                              className="text-xs"
                                            >
                                              +{provider.pools.length - 2} more
                                            </Badge>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-gray-500 text-xs">
                                          No pools
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <ProviderActionsMenu
                              displayName={displayName || "Provider"}
                              isHidden={isHidden}
                              canChat={provider.canChat}
                              canChangeStatus={Boolean(
                                canManageAffiliations &&
                                  selectedOrgBusinessUnit?.id &&
                                  provider.organizationBusinessUnitAffiliationId,
                              )}
                              isRevealChecking={
                                revealCheckingProviderId === provider.userId
                              }
                              onReveal={() =>
                                void openRevealDialog(provider.userId)
                              }
                              onChat={() => {
                                setChattingProvider({
                                  id: provider.userId,
                                  firstName: provider.firstName,
                                  lastName: provider.lastName,
                                });
                              }}
                              onViewJobInterests={() => {
                                setViewingJobPostsProvider({
                                  id: provider.userId,
                                  name: displayName || "Provider",
                                });
                              }}
                              onChangeStatus={() => {
                                const currentStatus = provider.isHired
                                  ? AffiliationStatus.Hired
                                  : AffiliationStatus.Candidate;
                                setStatusTarget({
                                  userId: provider.userId,
                                  name: displayName || "Provider",
                                  currentStatus,
                                  canMarkHired: !isHidden,
                                });
                                setSelectedProviderStatus(currentStatus);
                                setStatusStartDate(
                                  provider.hiredStartDate ?? "",
                                );
                              }}
                            />
                          </div>
                        </div>

                        {canManage &&
                          selectedOrgBusinessUnit?.id &&
                          provider.organizationBusinessUnitAffiliationId &&
                          (editingRowId === provider.id ? (
                            <div className="pt-3 border-t border-slate-100">
                              <div>
                                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                  Assigned
                                </span>
                                <Select
                                  value={provider.assignedUserId ?? "none"}
                                  onValueChange={(value) => {
                                    updateAssignedUser.mutate({
                                      organizationBusinessUnitAffiliationId:
                                        provider.organizationBusinessUnitAffiliationId!,
                                      organizationBusinessUnitId:
                                        selectedOrgBusinessUnit.id,
                                      assignedUserId:
                                        value === "none" ? null : value,
                                    });
                                  }}
                                >
                                  <SelectTrigger className="h-7 text-xs">
                                    <SelectValue placeholder="None" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {provider.assignedUserId &&
                                      !teamMembers?.some(
                                        (m) =>
                                          m.userId === provider.assignedUserId,
                                      ) && (
                                        <SelectItem
                                          value={provider.assignedUserId}
                                          disabled
                                        >
                                          {provider.assignedUserFirstName}{" "}
                                          {provider.assignedUserLastName ?? ""}{" "}
                                          (former)
                                        </SelectItem>
                                      )}
                                    {teamMembers?.map((member) => (
                                      <SelectItem
                                        key={member.userId}
                                        value={member.userId}
                                      >
                                        {member.firstName} {member.lastName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 pt-3 border-t border-slate-100 w-full">
                              <div className="text-sm text-slate-600 min-w-0 flex-1">
                                <p className="truncate">
                                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                    Assigned{" "}
                                  </span>
                                  {provider.assignedUserFirstName
                                    ? `${provider.assignedUserFirstName} ${provider.assignedUserLastName ?? ""}`
                                    : "—"}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                                onClick={() => setEditingRowId(provider.id)}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Desktop Table Layout */}
              <Card className="hidden lg:block p-0 border border-slate-200/80 shadow-sm overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 text-slate-600 uppercase tracking-wide text-xs">
                      <TableHead className="w-12"></TableHead>
                      <TableHead className="min-w-[150px]">Name</TableHead>
                      <TableHead className="min-w-[120px]">Specialty</TableHead>
                      <TableHead>Pools</TableHead>
                      <TableHead className="max-w-[200px]">Assigned</TableHead>
                      <TableHead className="w-[200px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProviders.map((provider) => {
                      const isHidden = provider.isRevealed === false;
                      const displayName = isHidden
                        ? `Provider #${provider.userId.slice(-4)}`
                        : `${provider.firstName ?? ""} ${provider.lastName ?? ""}`.trim();

                      return (
                        <TableRow
                          key={provider.id}
                          className="hover:bg-slate-50"
                        >
                          <TableCell className="py-3">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src="" alt="Provider" />
                              <AvatarFallback className="bg-gray-200">
                                <User className="h-5 w-5 text-gray-500" />
                              </AvatarFallback>
                            </Avatar>
                          </TableCell>
                          <TableCell className="py-3">
                            <Button
                              variant="link"
                              className="text-blue-600 hover:text-blue-700 font-semibold p-0 h-auto text-left justify-start max-w-[200px] min-w-0"
                              onClick={() => {
                                setViewingProvider({
                                  id: provider.userId,
                                });
                              }}
                            >
                              <span className="truncate">{displayName}</span>
                            </Button>
                            {provider.isHired && (
                              <div className="mt-1">
                                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">
                                  Hired
                                </Badge>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="text-sm">
                              {isHidden
                                ? "Hidden until revealed"
                                : provider.specialties[0]?.name ||
                                  "No specialty"}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <div className="flex items-center gap-2">
                              {!isHidden &&
                              provider.pools &&
                              provider.pools.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {provider.pools.slice(0, 2).map((pool) => (
                                    <Link
                                      to={orgPath(
                                        "/providers/pools/" + pool.id,
                                      )}
                                      key={pool.id}
                                    >
                                      <Badge
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        {pool.name}
                                      </Badge>
                                    </Link>
                                  ))}
                                  {provider.pools.length > 2 && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      +{provider.pools.length - 2} more
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">
                                  No pools
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-3 max-w-[200px]">
                            {editingRowId === provider.id &&
                            canManage &&
                            selectedOrgBusinessUnit?.id &&
                            provider.organizationBusinessUnitAffiliationId ? (
                              <div className="flex flex-col gap-1.5">
                                <div>
                                  <Select
                                    value={provider.assignedUserId ?? "none"}
                                    onValueChange={(value) => {
                                      updateAssignedUser.mutate({
                                        organizationBusinessUnitAffiliationId:
                                          provider.organizationBusinessUnitAffiliationId!,
                                        organizationBusinessUnitId:
                                          selectedOrgBusinessUnit.id,
                                        assignedUserId:
                                          value === "none" ? null : value,
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="h-7 text-xs">
                                      <SelectValue placeholder="None" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">None</SelectItem>
                                      {provider.assignedUserId &&
                                        !teamMembers?.some(
                                          (m) =>
                                            m.userId ===
                                            provider.assignedUserId,
                                        ) && (
                                          <SelectItem
                                            value={provider.assignedUserId}
                                            disabled
                                          >
                                            {provider.assignedUserFirstName}{" "}
                                            {provider.assignedUserLastName ??
                                              ""}{" "}
                                            (former)
                                          </SelectItem>
                                        )}
                                      {teamMembers?.map((member) => (
                                        <SelectItem
                                          key={member.userId}
                                          value={member.userId}
                                        >
                                          {member.firstName} {member.lastName}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <div className="text-sm text-slate-600 min-w-0 flex-1 truncate">
                                  {provider.assignedUserFirstName
                                    ? `${provider.assignedUserFirstName} ${provider.assignedUserLastName ?? ""}`
                                    : "—"}
                                </div>
                                {canManage &&
                                  selectedOrgBusinessUnit?.id &&
                                  provider.organizationBusinessUnitAffiliationId && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                                      onClick={() =>
                                        setEditingRowId(provider.id)
                                      }
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                  )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="py-3 text-right">
                            <ProviderActionsMenu
                              displayName={displayName || "Provider"}
                              isHidden={isHidden}
                              canChat={provider.canChat}
                              canChangeStatus={Boolean(
                                canManageAffiliations &&
                                  selectedOrgBusinessUnit?.id &&
                                  provider.organizationBusinessUnitAffiliationId,
                              )}
                              isRevealChecking={
                                revealCheckingProviderId === provider.userId
                              }
                              onReveal={() =>
                                void openRevealDialog(provider.userId)
                              }
                              onChat={() => {
                                setChattingProvider({
                                  id: provider.userId,
                                  firstName: provider.firstName,
                                  lastName: provider.lastName,
                                });
                              }}
                              onViewJobInterests={() => {
                                setViewingJobPostsProvider({
                                  id: provider.userId,
                                  name: displayName || "Provider",
                                });
                              }}
                              onChangeStatus={() => {
                                const currentStatus = provider.isHired
                                  ? AffiliationStatus.Hired
                                  : AffiliationStatus.Candidate;
                                setStatusTarget({
                                  userId: provider.userId,
                                  name: displayName || "Provider",
                                  currentStatus,
                                  canMarkHired: !isHidden,
                                });
                                setSelectedProviderStatus(currentStatus);
                                setStatusStartDate(
                                  provider.hiredStartDate ?? "",
                                );
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
              <div
                ref={loadMoreRef}
                className="flex min-h-10 items-center justify-center py-4 text-sm text-slate-500"
              >
                {isFetchingNextPage ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading more providers
                  </span>
                ) : hasNextPage ? (
                  "Scroll to load more providers"
                ) : (
                  "All matching providers loaded"
                )}
              </div>
            </>
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-blue-100 p-3 rounded-full mb-4">
                  <User className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">
                  No providers found
                </h3>
                <p className="text-gray-600 mb-6 max-w-md text-sm sm:text-base">
                  {hasFilters
                    ? "There are no providers matching your search criteria."
                    : "You don't have any providers shared with you yet."}
                </p>
                {hasFilters && (
                  <Button
                    variant="outline"
                    onClick={handleResetFilters}
                    className="flex items-center gap-2"
                  >
                    <FilterX size={16} />
                    Reset Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
      </div>
      {viewingProvider && (
        <ProfileModal
          userId={viewingProvider.id}
          onClose={() => setViewingProvider(null)}
          organizationBusinessUnitId={selectedOrgBusinessUnit?.id}
        />
      )}
      {revealTarget && selectedOrgBusinessUnit?.id && (
        <RevealProviderDialog
          open={revealTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setRevealTarget(null);
              setInitialMonthlyRevealLimit(null);
            }
          }}
          providerUserId={revealTarget}
          organizationBusinessUnitId={selectedOrgBusinessUnit.id}
          initialMonthlyRevealLimit={initialMonthlyRevealLimit}
          onRevealed={invalidateProviders}
        />
      )}
      {chattingProvider && (
        <ChatDialog
          isOpen={chattingProvider !== null}
          onOpenChange={() => setChattingProvider(null)}
          user={chattingProvider}
        />
      )}
      {viewingJobPostsProvider && selectedOrgBusinessUnit?.id && (
        <InterestedJobPostsDialog
          userId={viewingJobPostsProvider.id}
          organizationBusinessUnitId={selectedOrgBusinessUnit.id}
          providerName={viewingJobPostsProvider.name}
          open={viewingJobPostsProvider !== null}
          onOpenChange={(open) => {
            if (!open) {
              setViewingJobPostsProvider(null);
            }
          }}
        />
      )}
      <ChangeProviderStatusDialog
        open={statusTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeStatusDialog();
          }
        }}
        providerName={statusTarget?.name}
        currentStatus={statusTarget?.currentStatus}
        canMarkHired={statusTarget?.canMarkHired ?? false}
        selectedStatus={selectedProviderStatus}
        startDate={statusStartDate}
        endReason={endReason}
        isPending={isStatusSaving}
        onStatusChange={setSelectedProviderStatus}
        onStartDateChange={setStatusStartDate}
        onEndReasonChange={setEndReason}
        onCancel={closeStatusDialog}
        onConfirm={saveProviderStatus}
      />
    </div>
  );
}

// The directory is affiliation-only and requires both a business unit and
// ViewJobOffers permission. Users lacking either belong on the "Shared with
// Me" page instead — redirect them there rather than rendering the directory.
function ProvidersAllRouteGuard() {
  const { user } = useRouteContext({ from: "/_org" }) as {
    user: { id?: string; is_admin: boolean } | null | undefined;
  };
  const { hasPermission } = useRolePermissions(user);
  const { selectedOrgBusinessUnit } = useOrganization();
  const orgPath = useOrgPath();

  if (
    !hasPermission(Permission.ViewJobOffers) ||
    !selectedOrgBusinessUnit?.id
  ) {
    return <Navigate to={orgPath("/providers/shared")} />;
  }

  return <ProvidersAllPage />;
}

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/providers/all/",
)({
  validateSearch: providerFilterSearchSchema,
  component: ProvidersAllRouteGuard,
});
